import { useTranslation } from "react-i18next";
import { TrashIcon } from "@/components/ui/icon";
import { DeleteConfirmModal } from "@/components/ui/modal";
import { MoreActionItem, MoreActionPopover } from "@/components/common/more-action-popover";
import { cn } from "@/lib/utils";
import { RadioButton } from "@/components/common/radio-button";
import { ArrowDownToLine, MoreHorizontal } from "lucide-react";
import { Image } from "@/components/common/image";
import { useEffect, useRef, useState } from "react";
import { formatDateSmart } from "@/utils/date-formatter";
import { TextareaEditable } from "../common/textarea-editable";
import { Video } from "../common/video";
import { formatAudioTime } from "@/utils/format-audio-time";
import { ViewIcon } from "./assets";
import { IconButton } from "../common/icon-button";

interface ItemCardProps {
  id: number;
  name?: string;
  type: "video" | "image";
  time: number;
  duration?: number;
  videoCoverUrl?: string;
  url?: string;
  openSelected?: boolean;
  isSelected?: boolean;
  deleteLoading?: boolean;
  onPreview?: ({ multiple, id, rect }: { multiple: boolean, id: number, rect?: DOMRect }) => void;
  /** 选择状态改变回调 */
  onSelected?: (selected: { id: number; checked: boolean }) => void;
  /** 删除回调 */
  onDelete?: (id: number) => void;
  /** 下载回调 */
  onDownload?: (id: number) => void;
  /** 重命名回调 */
  onRename?: (id: number, name: string) => void;
}

export const ItemCard = ({
  id,
  name,
  type,
  time,
  deleteLoading,
  url,
  videoCoverUrl,
  duration,
  openSelected,
  isSelected,
  onSelected,
  onPreview,
  onDelete,
  onDownload,
}: ItemCardProps) => {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(isSelected || false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setChecked(isSelected || false);
  }, [isSelected]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
      }
    };
  }, []);

  const handleSelect = (value: boolean, id: number) => {
    setChecked(value);
    onSelected?.({ id, checked: value });
  }

  return (
    <div
      className={cn(
        "group w-full h-[195px] cursor-pointer group relative rounded-md",
        "hover:bg-[rgba(234,236,240,1)]",
        checked && "bg-[rgba(234,236,240,1)]",
      )}
      onClick={(e) => {
        e.stopPropagation()
        if (openSelected) {
          handleSelect(!checked, id);
        }
      }}
    >
      <div className="w-full h-full rounded-md flex flex-col items-center justify-center relative">
        {url && (
          <div className={cn(
            "w-full h-full rounded-md overflow-hidden flex items-center justify-center",
            checked && openSelected && "bg-[rgba(208,213,221,1)]",
          )}>
            {type === 'image' && (
              <Image
                src={url}
                className="max-h-full max-w-full rounded-[8px] object-contain border border-[rgba(0,0,0,0.1)]"
                onDragStart={(e) => e.preventDefault()}
                onClick={() => {
                  if (openSelected) return;
                  onPreview?.({ multiple: true, id })
                }}
                loading="lazy"
              />
            )}
            {type === 'video' && (
              <>
                <Video
                  ref={videoRef}
                  src={url}
                  poster={videoCoverUrl || ''}
                  className="max-h-full max-w-full rounded-[8px] object-contain "
                  preload="none"
                  onClick={() => {
                    if (openSelected) return;
                    onPreview?.({ multiple: true, id })
                  }}
                />
                <div className='absolute top-1 left-1 text-[rgba(255,255,255,1)] text-[9px] font-semibold border border-[#000000]/30 bg-[#000000]/20 px-1 py-0.5 rounded-[4.9px]'>{formatAudioTime(duration)}</div>
              </>
            )}
            <IconButton
              className={cn(
                "size-6 opacity-0 rounded-sm group-hover:opacity-100 absolute bottom-1 right-2 shadow-md transition-all hover:bg-white/60 bg-white/60",
              )}
              icon={<ViewIcon className="text-gray-700" />}
              onMouseEnter={(e) => {
                e.stopPropagation();
                // 清除之前的定时器
                if (previewTimerRef.current) {
                  clearTimeout(previewTimerRef.current);
                  previewTimerRef.current = null;
                }
                const rect = e.currentTarget.getBoundingClientRect();
                // 设置新的定时器，1秒后触发预览
                previewTimerRef.current = setTimeout(() => {
                  onPreview?.({ multiple: false, id, rect });
                }, 500);
              }}
              onMouseLeave={(e) => {
                e.stopPropagation();
                // 鼠标离开时清除定时器
                if (previewTimerRef.current) {
                  clearTimeout(previewTimerRef.current);
                  previewTimerRef.current = null;
                }
              }}
            />
          </div>
        )}
      </div>
      <div className="w-full absolute -bottom-2 translate-y-full">
        <div className="w-full text-center pt-1 text-sm text-[rgba(102,112,133,1)]">
          {formatDateSmart(time).replace(
            "Today",
            t("creation_folder.project_card.today")
          )}
        </div>
      </div>
      <RadioButton
        checked={checked}
        onChange={(value) => handleSelect(value, id)}
        className={cn(
          "absolute size-6 top-2 left-2 rounded-[5px] border-[3px] border-white bg-[rgba(0,0,0,0.25)]",
          "opacity-0 group-hover:opacity-100",
          checked && "opacity-100 bg-[rgba(90,92,96,1)]",
        )}
      />
      {/* 更多操作 */}
      <div
        className="opacity-0 group-hover:opacity-100 absolute top-1 right-2 transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        <MoreActionPopover
          className="w-40"
          side="bottom"
          tiggerIcon={
            <MoreHorizontal className="size-5 text-black" />
          }
          tiggerClassName="hover:bg-white/60 bg-white/60 shadow-md"
        >
          <MoreActionItem
            icon={<ArrowDownToLine className="w-4 h-4" />}
            label={t("common.download")}
            className='text-[rgba(16,24,40,1)] font-medium'
            onClick={() => onDownload?.(id)}
          />
          <DeleteConfirmModal
            trigger={
              <MoreActionItem
                icon={<TrashIcon className="w-4 h-4" />}
                className='text-[rgba(16,24,40,1)] font-medium'
                label={t("common.delete")}
              />
            }
            onConfirm={() => onDelete?.(id)}
            loading={deleteLoading}
          />
        </MoreActionPopover>
      </div>
    </div>
  )
}
